import type { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Card, Typography } from 'antd';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { HelpHotspot } from '@/features/help/HelpHotspot';

const { Text } = Typography;

interface KpiTileProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  accent?: 'ink' | 'violet' | 'green' | 'amber' | 'red';
  trend?: {
    direction: 'up' | 'down' | 'flat';
    label: string;
    /** Which way is good news. Default 'up' (green up, red down); 'neither' keeps it grey — e.g. spend. */
    goodWhen?: 'up' | 'down' | 'neither';
  };
  onClick?: () => void;
  // Topic key for the help drawer. When set, an inline (?) hotspot renders
  // next to the label so users can learn what this tile *means* without
  // leaving the page.
  helpTopic?: string;
  // Some KPIs are short labels (e.g. an audience name), not numbers. The
  // default 32px headline awkwardly truncates "Patient + Care team" — drop
  // to "medium" to use 18px and let it breathe.
  valueSize?: 'large' | 'medium';
}

// Hero KPI tile. Used on Dashboard + Agent Detail pages.
//
// Visual recipe:
//   - Soft tinted gradient background based on accent
//   - Top-left: small label (uppercase, tracked, secondary)
//   - Top-right: icon (semi-transparent)
//   - Big value in the center
//   - Bottom: optional hint or trend indicator
//   - Hover: lift 2px, shadow grows
//   - Click: optional navigation

const ACCENT_BG: Record<NonNullable<KpiTileProps['accent']>, { light: string; dark: string }> = {
  ink: {
    light: `linear-gradient(135deg, ${colors.ink[50]} 0%, #FFFFFF 70%)`,
    dark: `linear-gradient(135deg, ${colors.ink[700]}33 0%, ${colors.slate[800]} 70%)`,
  },
  violet: {
    light: `linear-gradient(135deg, ${colors.violet[50]} 0%, #FFFFFF 70%)`,
    dark: `linear-gradient(135deg, ${colors.violet[700]}33 0%, ${colors.slate[800]} 70%)`,
  },
  green: {
    light: `linear-gradient(135deg, ${colors.green[100]} 0%, #FFFFFF 70%)`,
    dark: `linear-gradient(135deg, ${colors.green[700]}33 0%, ${colors.slate[800]} 70%)`,
  },
  amber: {
    light: `linear-gradient(135deg, ${colors.amber[100]} 0%, #FFFFFF 70%)`,
    dark: `linear-gradient(135deg, ${colors.amber[700]}33 0%, ${colors.slate[800]} 70%)`,
  },
  red: {
    light: `linear-gradient(135deg, ${colors.red[100]} 0%, #FFFFFF 70%)`,
    dark: `linear-gradient(135deg, ${colors.red[700]}33 0%, ${colors.slate[800]} 70%)`,
  },
};

const ACCENT_BORDER: Record<NonNullable<KpiTileProps['accent']>, string> = {
  ink: colors.ink[100],
  violet: colors.violet[100],
  green: colors.green[100],
  amber: colors.amber[100],
  red: colors.red[100],
};

const ACCENT_ACTIVE: Record<NonNullable<KpiTileProps['accent']>, string> = {
  ink: colors.ink[600],
  violet: colors.violet[600],
  green: colors.green[600],
  amber: colors.amber[600],
  red: colors.red[600],
};

export const KpiTile = observer(function KpiTile({
  label,
  value,
  hint,
  icon,
  accent = 'ink',
  trend,
  onClick,
  helpTopic,
  valueSize = 'large',
}: KpiTileProps) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const bg = ACCENT_BG[accent][isDark ? 'dark' : 'light'];
  const border = isDark ? colors.slate[700] : ACCENT_BORDER[accent];
  const accentColor = ACCENT_ACTIVE[accent];

  // The Card sits inside a CSS grid row whose height is set by the TALLEST
  // sibling (the interactive AudienceTile / SkillRouterTile cards have more
  // content than a static-count tile, so they win). Without explicit
  // height-fill + space-between, the static cards' content clusters at the
  // top of the cell and leaves a visible empty band at the bottom.
  // Fix: make the body a full-height column with space-between distributing
  // label → value → hint across whatever height the row gives us.
  return (
    <Card
      hoverable={!!onClick}
      onClick={onClick}
      styles={{
        body: {
          padding: 18,
          minHeight: 120,
          height: '100%',
          background: bg,
          border: `1px solid ${border}`,
          borderRadius: 12,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 8,
        },
      }}
      style={{
        border: 'none',
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 120ms ease, box-shadow 120ms ease',
      }}
    >
      {/* Top row: label + icon */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <Text
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: isDark ? colors.slate[400] : colors.slate[500],
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {label}
          {helpTopic && (
            <span onClick={(e) => e.stopPropagation()}>
              <HelpHotspot topic={helpTopic} variant="inline" label={`Learn about ${label.toLowerCase()}`} />
            </span>
          )}
        </Text>
        {icon && (
          <div style={{ color: accentColor, opacity: 0.6, fontSize: 18, lineHeight: 1 }}>
            {icon}
          </div>
        )}
      </div>

      {/* Middle: the value. Centered vertically by space-between. */}
      <div
        style={{
          fontSize: valueSize === 'medium' ? 18 : 32,
          fontWeight: 700,
          color: isDark ? colors.slate[50] : colors.slate[900],
          lineHeight: 1.15,
          letterSpacing: valueSize === 'medium' ? '-0.005em' : '-0.02em',
          fontVariantNumeric: valueSize === 'medium' ? undefined : 'tabular-nums',
          wordBreak: 'break-word',
          // marginTop pushes the value down a notch so it sits visually a
          // hair below center (matches the optical balance of the
          // interactive sibling tiles' main content).
          marginTop: 'auto',
        }}
      >
        {value}
      </div>

      {/* Bottom row: hint + trend. Always pinned to the bottom of the card. */}
      {(hint || trend) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {hint && (
            <Text
              type="secondary"
              style={{ fontSize: 12, lineHeight: 1.4, color: isDark ? colors.slate[400] : colors.slate[500] }}
            >
              {hint}
            </Text>
          )}
          {trend && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color:
                  trend.direction === 'flat' || trend.goodWhen === 'neither'
                    ? colors.slate[500]
                    : trend.direction === (trend.goodWhen ?? 'up')
                    ? colors.green[600]
                    : colors.red[600],
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : '→'} {trend.label}
            </span>
          )}
        </div>
      )}
    </Card>
  );
});
